/**
 * NeckLateralFlexionEvaluator — Sprint 3 spec-medicion Bloque 4 (12/7/2026).
 *
 * Detector NUEVO para inclinación lateral de cuello (`mov_cuello`). Nada
 * que ver con el ExerciseEvaluator legacy (que oscila un ángulo entre 2
 * umbrales de un mismo lado); acá el ángulo cruza CERO — el vector
 * cabeza→hombros se inclina a un lado O al otro respecto a la vertical
 * del frame, y ambos lados cuentan.
 *
 * Mide: ángulo entre el vector midpoint(headA, headB) → midpoint(shoulderA,
 * shoulderB) y la vertical del frame. Signo del ángulo discrimina lado
 * (positivo/negativo), pero el count no distingue L o R — cuenta cada
 * inclinación clara. Distribución final por lado (spec: ~5 por lado) se
 * reporta en el summary post-sesión, no se enforce durante ejecución.
 *
 * Sin `rules` (schema.rules es []). Reglas de forma clínicas quedan
 * pendientes para clinical review con Juan (spec-medicion Q3). Este
 * evaluator solo cuenta reps.
 */

import type {
  EvaluationResult,
  Evaluator,
  ExerciseSchema,
  Keypoint,
  NeckLateralFlexionDetector,
  PoseResult,
  RepResult,
} from "./types";

/** Versión semántica del evaluator. Se persiste en pose_analysis.detector
 *  al cerrar sesión para trazabilidad. Subir cuando cambie la lógica del
 *  state machine o se calibren tolerancias significativamente. */
export const EVALUATOR_VERSION = "iter1";

const MIN_VISIBILITY = 0.5;
/** Fracción del thresholdDeg debajo de la cual se considera que el
 *  paciente empezó a volver desde el pico (transición pico → excentrica). */
const PEAK_RELEASE_FACTOR = 0.6;

type NeckPhase = "inicio" | "concentrica" | "pico" | "excentrica";

export class NeckLateralFlexionEvaluator implements Evaluator {
  readonly version = EVALUATOR_VERSION;

  private schema: ExerciseSchema;
  private detector: NeckLateralFlexionDetector;

  private phase: NeckPhase = "inicio";
  private repCount = 0;

  private repInProgress: {
    side: 1 | -1;
    peakAngle: number;
    startTs: number;
    dwellStartTs: number;
  } | null = null;

  constructor(schema: ExerciseSchema) {
    if (!schema.repDetector || schema.repDetector.type !== "neck_lateral_flexion") {
      throw new Error(
        `NeckLateralFlexionEvaluator requiere schema.repDetector.type === "neck_lateral_flexion" ` +
          `(schema id: ${schema.id}).`,
      );
    }
    this.schema = schema;
    this.detector = schema.repDetector;
  }

  reset(): void {
    this.phase = "inicio";
    this.repCount = 0;
    this.repInProgress = null;
  }

  evaluate(pose: PoseResult): EvaluationResult {
    const det = this.detector;
    const kp = pose.keypoints;

    const headA = kp[det.headA];
    const headB = kp[det.headB];
    const shoulderA = kp[det.shoulderA];
    const shoulderB = kp[det.shoulderB];

    // Visibility gate — si algún landmark clave está flojo, no evaluamos
    // este frame (evita ruido angular por keypoints mal detectados).
    if (
      !visible(headA) ||
      !visible(headB) ||
      !visible(shoulderA) ||
      !visible(shoulderB)
    ) {
      return {
        ruleEvaluations: [],
        repCount: this.repCount,
        repPhase: this.phase,
        debug: {
          visibility: "LOW",
          headA_v: headA?.visibility?.toFixed(2) ?? "—",
          headB_v: headB?.visibility?.toFixed(2) ?? "—",
          shA_v: shoulderA?.visibility?.toFixed(2) ?? "—",
          shB_v: shoulderB?.visibility?.toFixed(2) ?? "—",
        },
      };
    }

    const headMid = midpoint(headA, headB);
    const shoulderMid = midpoint(shoulderA, shoulderB);

    // Vector cabeza → hombros. En neutro (cabeza sobre hombros), dx ≈ 0
    // y dy > 0 (Y crece hacia abajo en coordenadas de frame). El ángulo
    // respecto a la vertical usa atan2(dx, dy): 0° = neutro, ± = inclinado.
    const dx = shoulderMid.x - headMid.x;
    const dy = shoulderMid.y - headMid.y;
    const angleDeg = Math.atan2(dx, dy) * (180 / Math.PI);

    const absAngle = Math.abs(angleDeg);
    const side: 1 | -1 = angleDeg >= 0 ? 1 : -1;

    let completedRep: RepResult | undefined;

    switch (this.phase) {
      case "inicio":
        // Salió de la zona neutra → arranca rep.
        if (absAngle >= det.neutralZoneDeg) {
          this.phase = "concentrica";
          this.repInProgress = {
            side,
            peakAngle: absAngle,
            startTs: pose.timestamp,
            dwellStartTs: 0,
          };
        }
        break;

      case "concentrica": {
        const rep = this.repInProgress!;
        // Cambio de lado en el medio → abort (probablemente ruido o
        // rebote no intencional). Volver a inicio.
        if (side !== rep.side) {
          this.phase = "inicio";
          this.repInProgress = null;
          break;
        }
        if (absAngle > rep.peakAngle) rep.peakAngle = absAngle;

        // Volvió a neutro sin cruzar threshold → abort. Ejemplo:
        // paciente movió apenas la cabeza pero no llegó a inclinar.
        if (absAngle < det.neutralZoneDeg) {
          this.phase = "inicio";
          this.repInProgress = null;
          break;
        }

        // Cruzó threshold → arranca ventana dwell.
        if (absAngle >= det.thresholdDeg) {
          if (rep.dwellStartTs === 0) {
            rep.dwellStartTs = pose.timestamp;
          }
          if (pose.timestamp - rep.dwellStartTs >= det.dwellMs) {
            this.phase = "pico";
          }
        } else {
          // Salió del threshold antes de completar dwell → reset dwell
          // timer, pero seguimos en concentrica.
          rep.dwellStartTs = 0;
        }
        break;
      }

      case "pico": {
        const rep = this.repInProgress!;
        if (side === rep.side && absAngle > rep.peakAngle) {
          rep.peakAngle = absAngle;
        }
        // Empezó a volver hacia neutro (bajó suficiente).
        if (absAngle < det.thresholdDeg * PEAK_RELEASE_FACTOR) {
          this.phase = "excentrica";
        }
        break;
      }

      case "excentrica": {
        const rep = this.repInProgress!;
        if (absAngle < det.neutralZoneDeg) {
          // REP COMPLETA
          this.repCount++;
          completedRep = {
            n: this.repCount,
            angleMax: rep.peakAngle,
            durationMs: pose.timestamp - rep.startTs,
            // Sin rules de forma → siempre válida técnicamente. Cuando
            // Juan defina reglas clínicas (Q3), quality se deriva de
            // acá.
            quality: 1.0,
            violations: [],
            angleAtStart: 0,
            prominence: rep.peakAngle,
            valid: true,
          };
          this.phase = "inicio";
          this.repInProgress = null;
        }
        break;
      }
    }

    const rep = this.repInProgress;
    return {
      ruleEvaluations: [],
      repCount: this.repCount,
      repPhase: this.phase,
      completedRep,
      debug: {
        angle: angleDeg.toFixed(1),
        abs: absAngle.toFixed(1),
        side: absAngle < det.neutralZoneDeg ? "—" : side > 0 ? "L" : "R",
        neutral: det.neutralZoneDeg,
        threshold: det.thresholdDeg,
        peak: rep ? rep.peakAngle.toFixed(1) : "—",
        dwell: rep && rep.dwellStartTs > 0
          ? Math.max(0, pose.timestamp - rep.dwellStartTs).toFixed(0)
          : "—",
      },
    };
  }
}

function visible(kp: Keypoint | undefined): kp is Keypoint {
  return !!kp && kp.visibility >= MIN_VISIBILITY;
}

function midpoint(a: Keypoint, b: Keypoint): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
